import { z } from 'zod';
/**
 * Vocabulary shared by every endpoint: the error envelope, pagination, and the filter values the
 * catalog and search routes both accept.
 *
 * These schemas drive three things at once — runtime validation, the TypeScript types the API and
 * the web app compile against, and the OpenAPI document. Anything defined twice would drift, so
 * nothing here is defined twice.
 */
/**
 * Stable machine-readable error codes.
 *
 * SCREAMING_SNAKE and never reworded, because a client branches on these. The human-readable
 * `message` is free to change.
 */
export const ERROR_CODES = [
    'VALIDATION_FAILED',
    'NOT_FOUND',
    'UNAUTHENTICATED',
    'FORBIDDEN',
    /** An email that already has an account. The first 409 in the API arrived with invitations. */
    'CONFLICT',
    'RATE_LIMITED',
    'SERVICE_UNAVAILABLE',
    'INTERNAL_ERROR',
];
export const errorCodeSchema = z.enum(ERROR_CODES);
/**
 * The one response shape for every failure.
 *
 * `details` carries field-level validation problems and nothing else. It never carries a stack
 * trace, a SQL statement or a driver message — those are logged server-side and stay there.
 */
export const errorResponseSchema = z
    .object({
    error: z.object({
        code: errorCodeSchema,
        message: z.string(),
        details: z
            .array(z.object({ path: z.string(), message: z.string() }))
            .optional()
            .describe('Field-level validation failures. Present only for VALIDATION_FAILED.'),
        requestId: z
            .string()
            .optional()
            .describe('Quote this when reporting a 500; it is the key into the server logs.'),
    }),
})
    .describe('Error envelope');
export const courseLevelSchema = z.enum(['beginner', 'intermediate', 'advanced']);
export const matchedOnSchema = z
    .enum(['both', 'vector', 'lexical', 'filter'])
    .describe('Which retrieval arm found this result. "filter" means it was browsed, not searched.');
export const MAX_PAGE_SIZE = 100;
export const DEFAULT_PAGE_SIZE = 20;
/**
 * A public identifier in a path segment — a UUID, an ISBN-13, a slug or a workbook code.
 *
 * Control characters are rejected rather than passed through. PostgreSQL cannot hold a NUL byte
 * in a `text` parameter at all, so `GET /courses/%00` reaches the driver and comes back as a
 * transport failure — a 500 for what is unambiguously a bad request. Rejecting the byte at the
 * edge turns it into the 400 it always was.
 *
 * The `example` is not decoration. Swagger UI generates its sample values from the schema, and
 * when a schema carries a `pattern` and nothing else it feeds the regex to a random string
 * generator — so `^[^\p{Cc}]+$`, which means "anything but a control character", produces
 * something like `,Sou~j[J<:W!?w2Ji$t%`. An interactive document whose Try-It body has to be
 * deleted before it can be used is worse than no document.
 */
export const identifierSchema = z
    .string()
    .min(1)
    .max(256)
    .regex(/^[^\p{Cc}]+$/u, 'must not contain control characters')
    .meta({ example: '9780137928576' });
/**
 * Free text arriving from a query string.
 *
 * Same reasoning as {@link identifierSchema}, on both counts: a control character in a search
 * term reaches `websearch_to_tsquery` and fails at the driver rather than at the edge, and a
 * pattern without an example turns the documentation into line noise. Length is capped because
 * an unbounded search term is an unbounded `tsquery`.
 */
export function safeText(maxLength, example = 'kubernetes') {
    return z
        .string()
        .trim()
        .min(1)
        .max(maxLength)
        .regex(/^[^\p{Cc}]+$/u, 'must not contain control characters')
        .meta({ example });
}
/**
 * `limit` is **rejected** above the maximum rather than silently clamped.
 *
 * Clamping makes a client that asked for 500 believe it received all 500 and stop paginating.
 * A 400 is noisier and correct.
 */
export const paginationQuerySchema = z.object({
    limit: z.coerce.number().int().min(1).max(MAX_PAGE_SIZE).default(DEFAULT_PAGE_SIZE),
    offset: z.coerce.number().int().min(0).max(10_000).default(0),
});
/**
 * A query-string boolean.
 *
 * `z.coerce.boolean()` is useless here: it is `Boolean(value)`, so the string `'false'` coerces
 * to `true`. Both spellings have to be enumerated explicitly.
 */
export const booleanQuerySchema = z
    .enum(['true', 'false', '1', '0'])
    .transform((value) => value === 'true' || value === '1');
/**
 * A repeatable query parameter that also accepts one comma-separated value.
 *
 * `?level=beginner&level=advanced` and `?level=beginner,advanced` are both idiomatic and both
 * turn up in hand-written URLs, so both are accepted and normalised to an array.
 *
 * `inner` must take a **string** as its input, because that is all a query string can carry. A
 * numeric parameter is therefore a string schema with a `.transform(Number)`, not
 * `z.coerce.number()` — which would type the OpenAPI parameter as `unknown`.
 */
export function csvArray(inner) {
    return z
        .union([z.string(), z.array(z.string())])
        .transform((value) => (Array.isArray(value) ? value : [value]).flatMap((entry) => entry
        .split(',')
        .map((part) => part.trim())
        .filter((part) => part.length > 0)))
        .pipe(z.array(inner).max(50));
}
/** Reference data for the filter UI. */
export const skillSchema = z.object({
    id: z.string(),
    name: z.string(),
    slug: z.string(),
    source: z.enum(['spreadsheet', 'llm']),
    courseCount: z.number().int().nonnegative(),
});
export const authorSchema = z.object({
    name: z.string(),
    courseCount: z.number().int().nonnegative(),
});
export const sowSchema = z.object({
    number: z.number().int(),
    label: z.string(),
    sheetName: z.string(),
    courseCount: z.number().int().nonnegative(),
});
export const skillListResponseSchema = z.object({ items: z.array(skillSchema) });
export const authorListResponseSchema = z.object({ items: z.array(authorSchema) });
export const sowListResponseSchema = z.object({ items: z.array(sowSchema) });
export const healthResponseSchema = z.object({
    status: z.literal('ok'),
    uptimeSeconds: z.number(),
});
export const readinessResponseSchema = z.object({
    status: z.enum(['ready', 'degraded']),
    checks: z.object({
        database: z.object({
            ok: z.boolean(),
            latencyMs: z.number().optional(),
            error: z.string().optional(),
        }),
    }),
});
//# sourceMappingURL=common.js.map